import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Grid,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Paper,
  CircularProgress,
  Box,
} from "@material-ui/core";
import A_TextField from "../../../../../Atoms/Inputs/TextFields/A_TextField";
import A_Select from "../../../../../Atoms/Inputs/Selects/A_Select";
import A_Button from "../../../../../Atoms/Inputs/Buttons/A_Button";
import A_CheckBox from "../../../../../Atoms/Inputs/Checkboxes/A_Checkbox";
import * as toastActions from "../../../../../../../store/actions/toastActions";
import { useSelector } from "react-redux";
import useStyles from "./AddPaymentDetails.styles";
import {
  buildBorrowerOptionSetObject,
  ExecuteSqlStoreProc,
  toOptionLabel,
} from "../../Add_Borrower/Borrower_Form_helper";
import { getCRMContactInfo } from "../../../../../../../Utils/Helpers/common";
import { useParams } from "react-router";
import { toastSeverity } from "../../../../../../../Utils/config/toastConfig";
import {
  submitPaymentDetails,
  lookupBankBySortCode,
} from "./AddPaymentDetails.helper";
const PAYMENT_DETAILS_STEPS = ["Borrower Details", "Third Party Details"];
const SORT_CODE_REGEX = /^\d{2}-\d{2}-\d{2}$/;
const SORT_CODE_FORMAT_ERROR =
  "Please enter the Sort Code in the format 12-34-56";

const AddPaymentDetails = forwardRef((props, ref) => {
  const classes = useStyles();
  const { afterClose, setToast, isEditMode, recordId } = props;
  const apiUrl = process.env.BANK_FINDER_API_URL;
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [enumoptions, setEnumOptions] = useState({});
  const [apiError, setApiError] = useState("");
  const [prefilledThirdPartyCount, setPrefilledThirdPartyCount] = useState(0);

  const [thirdPartyLoading, setThirdPartyLoading] = useState({});
  const [isSortCodeLookupLoading, setIsSortCodeLookupLoading] = useState(false);
  const [bankCountryInputMode, setBankCountryInputMode] = useState("manual");
  const [isBankDetailsReadOnly, setIsBankDetailsReadOnly] = useState(false);
  const lastLookedUpSortCodeRef = useRef("");
  const user = useSelector((state) => state.oidc.user);
  const { crmContactUserEmail } = getCRMContactInfo();
  let logedInUserData = {
    createdBy: crmContactUserEmail || user?.profile?.email || "",
    createdDate: Math.floor(Date.now() / 1000),
  };

  let { crmId, pageUrl } = useParams();
  useEffect(() => {
    getEnumOptionsForField();
  }, []);

  const getEnumOptionsForField = async () => {
    const opionsetCategories =
      "ThirdPartyBeneficiaryType,Country,BankVerificationStatus,BankType,ThirdPartyType";
    try {
      setIsLoading(true);
      const payload = [
        {
          id: "Borrower_Dropdowns",
          spName: "[DF].[usp_GetOptionSetsByCategories]",
          parameters: {
            Categories: opionsetCategories,
          },
        },
      ];

      if (isEditMode) {
        payload.push({
          id: "Prefilled_Payment_Details",
          spName: "DF.usp_PrefilledPaymentDetails",
          parameters: {
            id: recordId,
          },
        });
      }

      const response = await ExecuteSqlStoreProc(payload);

      // Handle Dropdowns
      const dropdownData = response?.Borrower_Dropdowns || [];
      const categories = opionsetCategories.split(",").map((c) => c.trim());
      const optionSetObject = buildBorrowerOptionSetObject(
        categories,
        dropdownData
      );

      if (Object.keys(optionSetObject).length !== 0) {
        setEnumOptions(optionSetObject);
        setApiError("");

        // Pre-select "Unverified" for Bank Verification Status if not in edit mode
        if (!isEditMode && optionSetObject["BankVerificationStatus"]) {
          const unverifiedOpt = optionSetObject["BankVerificationStatus"].find(
            (opt) =>
              opt?.label?.toString().toLowerCase().includes("unverified") ||
              opt?.value?.toString().toLowerCase().includes("unverified")
          );
          if (unverifiedOpt) {
            setFormData((prev) => ({
              ...prev,
              BorrowerBankVerificationStatus: unverifiedOpt.value,
            }));
          }
        }
      } else {
        setEnumOptions([]);
      }

      // Handle Prefilled Data
      const prefilledData = response?.Prefilled_Payment_Details?.[0];
      if (prefilledData) {
        setFormData((prev) => ({
          ...prev,
          ...prefilledData,
        }));

        const bankFoundValue = prefilledData.found;
        const isBankFound =
          bankFoundValue === true ||
          bankFoundValue === "true" ||
          bankFoundValue === 1 ||
          bankFoundValue === "1";
        setIsBankDetailsReadOnly(isBankFound);
        setBankCountryInputMode(isBankFound ? "api" : "manual");
        if (prefilledData.BorrowerSortCode) {
          lastLookedUpSortCodeRef.current = prefilledData.BorrowerSortCode;
        }

        if (prefilledData.HasThirdParty) {
          const count = parseInt(prefilledData.ThirdPartyCount) || 0;
          setPrefilledThirdPartyCount(count);
          for (let i = 0; i < count; i++) {
            const thirdpartytype = prefilledData[`ThirdPartyType_${i}`];
            const thirdpartyname = prefilledData[`ThirdPartyName_${i}`];

            let parsedType = thirdpartytype;
            let parsedName = thirdpartyname;

            if (
              typeof thirdpartytype === "string" &&
              thirdpartytype.startsWith("{")
            ) {
              try {
                parsedType = JSON.parse(thirdpartytype);
              } catch (e) {
                parsedType = thirdpartytype;
              }
            }
            if (
              typeof thirdpartyname === "string" &&
              thirdpartyname.startsWith("{")
            ) {
              try {
                parsedName = JSON.parse(thirdpartyname);
              } catch (e) {
                parsedName = thirdpartyname;
              }
            }

            setFormData((prev) => ({
              ...prev,
              [`ThirdPartyType_${i}`]: parsedType,
              [`ThirdPartyName_${i}`]: parsedName,
            }));

            if (parsedType) {
              const typeStr =
                typeof parsedType === "object" ? parsedType.value : parsedType;
              if (typeStr) {
                loadThirdPartyNameOptions(typeStr, i);
              }
            }
          }
        }
      }
    } catch (error) {
      setEnumOptions([]);
      setApiError("Failed to load form options. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const getEnumOptions = (field) => {
    const optionsFromEnum = enumoptions[field];
    if (
      !optionsFromEnum ||
      (Array.isArray(optionsFromEnum) && optionsFromEnum.length === 0)
    ) {
      return [];
    }
    if (Array.isArray(optionsFromEnum)) {
      return optionsFromEnum.map((data) => ({
        id: data?.id,
        label: toOptionLabel(data?.label),
        value: data?.value,
      }));
    }
    return [];
  };

  const getThirdPartyNameOptions = (index) => {
    const thirdPartyNames = enumoptions.ThirdPartyName;
    if (!thirdPartyNames || !thirdPartyNames[index]) {
      return [];
    }
    return thirdPartyNames[index].map((data) => ({
      id: data?.id,
      label: toOptionLabel(data?.label),
      value: data?.value,
    }));
  };

  const loadThirdPartyNameOptions = async (thirdPartyType, index) => {
    setThirdPartyLoading((prev) => ({ ...prev, [index]: true }));
    try {
      const payload = [
        {
          id: "third_party_names",
          spName: "DF.usp_FormLookupField",
          parameters: {
            lookupName: thirdPartyType,
          },
        },
      ];
      const response = await ExecuteSqlStoreProc(payload);

      setEnumOptions((prev) => ({
        ...prev,
        ThirdPartyName: {
          ...prev.ThirdPartyName,
          [index]: response?.third_party_names || [],
        },
      }));
    } catch (error) {
      const dummyNames = {
        Type1: [
          { label: "Name A", value: "NameA" },
          { label: "Name B", value: "NameB" },
        ],
        Type2: [
          { label: "Name C", value: "NameC" },
          { label: "Name D", value: "NameD" },
        ],
        Type3: [
          { label: "Name E", value: "NameE" },
          { label: "Name F", value: "NameF" },
        ],
      };

      setEnumOptions((prev) => ({
        ...prev,
        ThirdPartyName: {
          ...prev.ThirdPartyName,
          [index]: dummyNames[thirdPartyType] || [],
        },
      }));
    } finally {
      setThirdPartyLoading((prev) => ({ ...prev, [index]: false }));
    }
  };

  useImperativeHandle(ref, () => ({
    hasUnsavedChanges: () => {
      return Object.keys(formData).length > 0;
    },
  }));

  const handleInputChange = (fieldId, value) => {
    if (fieldId === "HasThirdParty" && value === false) {
      setFormData((prev) => ({
        ...prev,
        HasThirdParty: false,
        ThirdPartyCount: "",
      }));
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.ThirdPartyCount;
        for (let i = 0; i < 5; i++) {
          delete newErrors[`ThirdPartyType_${i}`];
          delete newErrors[`ThirdPartyName_${i}`];
        }
        return newErrors;
      });
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
    if (errors[fieldId]) {
      setErrors((prev) => ({
        ...prev,
        [fieldId]: "",
      }));
    }
    if (apiError) {
      setApiError("");
    }
  };

  const handleThirdPartyTypeChange = (data, index) => {
    const newFormData = { ...formData };
    newFormData[`ThirdPartyType_${index}`] = data || "";
    newFormData[`ThirdPartyName_${index}`] = "";
    setFormData(newFormData);
    loadThirdPartyNameOptions(data?.value, index);
  };

  const handleThirdPartyCountChange = (value) => {
    if (value === "") {
      if (prefilledThirdPartyCount > 0) {
        handleInputChange(
          "ThirdPartyCount",
          prefilledThirdPartyCount.toString()
        );
        setErrors((prev) => ({
          ...prev,
          ThirdPartyCount:
            "You can add a third party here and remove them using the delete option in the table",
        }));
        return;
      }
      handleInputChange("ThirdPartyCount", "");
      return;
    }
    const rawCount = parseInt(value) || 0;

    if (rawCount < prefilledThirdPartyCount) {
      setErrors((prev) => ({
        ...prev,
        ThirdPartyCount:
          "You can add a third party here and remove them using the delete option in the table",
      }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.ThirdPartyCount;
        return newErrors;
      });
    }

    const minCount = Math.max(0, prefilledThirdPartyCount);
    const count = Math.min(5, Math.max(minCount, rawCount));

    setFormData((prev) => ({
      ...prev,
      ThirdPartyCount: count.toString(),
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      const requiredFields = [
        "BorrowerBeneficiaryName",
        "BorrowerAccountNumber",
        "BorrowerSortCode",
        "BorrowerBankType",
        "BorrowerBankName",
        "BorrowerBankTown",
        "BorrowerBankPostcode",
        "BorrowerBankCountry",
        "BorrowerBankAddress",
        "BorrowerAddress",
        "BorrowerTown",
        "BorrowerPostcode",
        "BorrowerPaymentCountry",
      ];
      requiredFields.forEach((field) => {
        if (!formData[field]) {
          newErrors[field] = "This field is required";
        }
      });

      if (
        formData.BorrowerSortCode &&
        !SORT_CODE_REGEX.test(formData.BorrowerSortCode)
      ) {
        newErrors.BorrowerSortCode = SORT_CODE_FORMAT_ERROR;
      }
    }

    if (step === 1 && formData.HasThirdParty) {
      const thirdPartyCount = parseInt(formData.ThirdPartyCount) || 0;
      if (!formData.ThirdPartyCount || thirdPartyCount <= 0) {
        newErrors.ThirdPartyCount =
          "This field is required and must be at least 1";
      }
      for (let i = 0; i < thirdPartyCount; i++) {
        if (!formData[`ThirdPartyType_${i}`]) {
          newErrors[`ThirdPartyType_${i}`] = "This field is required";
        }
        if (!formData[`ThirdPartyName_${i}`]) {
          newErrors[`ThirdPartyName_${i}`] = "This field is required";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      if (activeStep === PAYMENT_DETAILS_STEPS.length - 1) {
        handleSubmit();
      } else {
        setActiveStep((prev) => prev + 1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(0)) {
      return;
    }

    setIsSubmitting(true);
    try {
      let payload = {
        ...formData,
        loanId: crmId,
        pageUrl: pageUrl,
        ...logedInUserData,
        BorrowerBeneficiaryType: "Borrower",
      };

      if (isEditMode) {
        payload.id = recordId;
      }

      if (!formData.HasThirdParty) {
        delete payload.HasThirdParty;
        delete payload.ThirdPartyCount;
        for (let i = 0; i < 5; i++) {
          delete payload[`ThirdPartyType_${i}`];
          delete payload[`ThirdPartyName_${i}`];
        }
      } else {
        const thirdPartyCount = parseInt(formData.ThirdPartyCount) || 0;
        for (let i = thirdPartyCount; i < 5; i++) {
          delete payload[`ThirdPartyType_${i}`];
          delete payload[`ThirdPartyName_${i}`];
        }
      }
      const response = await submitPaymentDetails(isEditMode, payload);
      if (response?.response?.data?.Message) {
        setApiError(
          response?.response?.data?.Message || "Something went wrong"
        );
      } else {
        setToast({
          type: toastSeverity.SUCCESS,
          severity: toastSeverity.SUCCESS,
          message: "Payment Details Added Successfully",
          status: true,
        });
        afterClose({ pageUrl, crmId });
      }
    } catch (error) {
      setApiError(error?.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectChange = (selectedOption, id) => {
    handleInputChange(id, selectedOption?.value || "");
  };

  const handleTextChange = (event, fieldName) => {
    handleInputChange(fieldName, event.target.value);
  };

  const handleAccountNumberChange = (event) => {
    const value = event.target.value.replace(/\D/g, "");
    handleInputChange("BorrowerAccountNumber", value);
  };

  const handleSortCodeChange = async (event) => {
    const value = event.target.value;
    handleInputChange("BorrowerSortCode", value);

    if (!value) {
      lastLookedUpSortCodeRef.current = "";
      return;
    }

    if (!SORT_CODE_REGEX.test(value)) {
      if (value.length >= 8) {
        setErrors((prev) => ({
          ...prev,
          BorrowerSortCode: SORT_CODE_FORMAT_ERROR,
        }));
      }
      return;
    }

    if (lastLookedUpSortCodeRef.current === value) {
      return;
    }
    lastLookedUpSortCodeRef.current = value;

    if (!apiUrl) {
      if (isBankDetailsReadOnly) {
        setFormData((prev) => ({
          ...prev,
          BorrowerBankName: "",
          BorrowerBankTown: "",
          BorrowerBankPostcode: "",
          BorrowerBankCountry: "",
        }));
      }
      handleInputChange("found", false);
      setBankCountryInputMode("manual");
      setIsBankDetailsReadOnly(false);
      return;
    }

    setIsSortCodeLookupLoading(true);
    try {
      const result = await lookupBankBySortCode(
        value.replace(/-/g, ""),
        apiUrl
      );
      if (result?.found) {
        const hasCountry = Boolean(result.country);
        setFormData((prev) => ({
          ...prev,
          BorrowerBankName: result.bankName || prev.BorrowerBankName,
          BorrowerBankTown: result.town || prev.BorrowerBankTown,
          BorrowerBankPostcode: result.postcode || prev.BorrowerBankPostcode,
          BorrowerBankCountry: hasCountry
            ? result.country
            : prev.BorrowerBankCountry,
          found: true,
        }));
        setBankCountryInputMode(hasCountry ? "api" : "manual");
        setIsBankDetailsReadOnly(true);
        setApiError("");
      } else {
        if (isBankDetailsReadOnly) {
          // Previous bank details came from a different sort code's lookup -
          // they no longer apply, so clear them rather than leaving stale data.
          setFormData((prev) => ({
            ...prev,
            BorrowerBankName: "",
            BorrowerBankTown: "",
            BorrowerBankPostcode: "",
            BorrowerBankCountry: "",
          }));
        }
        handleInputChange("found", false);
        setBankCountryInputMode("manual");
        setIsBankDetailsReadOnly(false);
        setApiError(result?.error || "No bank found for this sort code");
      }
    } catch (error) {
      if (isBankDetailsReadOnly) {
        setFormData((prev) => ({
          ...prev,
          BorrowerBankName: "",
          BorrowerBankTown: "",
          BorrowerBankPostcode: "",
          BorrowerBankCountry: "",
        }));
      }
      handleInputChange("found", false);
      setBankCountryInputMode("manual");
      setIsBankDetailsReadOnly(false);
      setApiError("Failed to look up bank details. Please try again.");
    } finally {
      setIsSortCodeLookupLoading(false);
    }
  };

  const renderBorrowerDetailsFields = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerBeneficiaryName"
          name="BorrowerBeneficiaryName"
          label="Beneficiary Name"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerBeneficiaryName}
          inputType="value"
          onChange={(e) => handleTextChange(e, "BorrowerBeneficiaryName")}
          required={true}
          error={Boolean(errors.BorrowerBeneficiaryName)}
          helperText={errors.BorrowerBeneficiaryName}
          // disabled={isPrefilled}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_Select
          id="BorrowerBankType"
          name="BorrowerBankType"
          label="Bank Type"
          fullWidth
          variant="standard"
          required={true}
          defaultValue={formData.BorrowerBankType || ""}
          options={getEnumOptions("BankType")}
          onChange={(selectedOption) =>
            handleSelectChange(selectedOption, "BorrowerBankType")
          }
          error={Boolean(errors.BankType)}
          helperText={errors.BankType}
          // disabled={isPrefilled}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerAccountNumber"
          name="BorrowerAccountNumber"
          label="Account Number"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerAccountNumber}
          inputType="value"
          inputMaxLength={10}
          onChange={handleAccountNumberChange}
          required={true}
          error={Boolean(errors.BorrowerAccountNumber)}
          helperText={errors.BorrowerAccountNumber}
          // disabled={isPrefilled}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerSortCode"
          name="BorrowerSortCode"
          label="Sort Code"
          placeholder="12-34-56"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerSortCode}
          inputType="value"
          onChange={handleSortCodeChange}
          required={true}
          error={Boolean(errors.BorrowerSortCode)}
          helperText={
            isSortCodeLookupLoading
              ? "Fetching bank details, please wait..."
              : errors.BorrowerSortCode
          }
          // disabled={isPrefilled}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerBankName"
          name="BorrowerBankName"
          label="Creditor Bank Name"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerBankName}
          inputType="value"
          onChange={(e) => handleTextChange(e, "BorrowerBankName")}
          required={true}
          disabled={isSortCodeLookupLoading || isBankDetailsReadOnly}
          error={Boolean(errors.BorrowerBankName)}
          helperText={errors.BorrowerBankName}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerBankTown"
          name="BorrowerBankTown"
          label="Creditor Bank Town"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerBankTown}
          inputType="value"
          onChange={(e) => handleTextChange(e, "BorrowerBankTown")}
          required={true}
          disabled={isSortCodeLookupLoading || isBankDetailsReadOnly}
          error={Boolean(errors.BorrowerBankTown)}
          helperText={errors.BorrowerBankTown}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerBankPostcode"
          name="BorrowerBankPostcode"
          label="Creditor Bank Postcode"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerBankPostcode}
          inputType="value"
          onChange={(e) => handleTextChange(e, "BorrowerBankPostcode")}
          required={true}
          disabled={isSortCodeLookupLoading || isBankDetailsReadOnly}
          error={Boolean(errors.BorrowerBankPostcode)}
          helperText={errors.BorrowerBankPostcode}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        {bankCountryInputMode === "api" ? (
          <A_TextField
            id="BorrowerBankCountry"
            name="BorrowerBankCountry"
            label="Creditor Bank Country"
            variant="standard"
            fullWidth
            defaultValue={formData.BorrowerBankCountry}
            inputType="value"
            onChange={(e) => handleTextChange(e, "BorrowerBankCountry")}
            required={true}
            disabled={isSortCodeLookupLoading || isBankDetailsReadOnly}
            error={Boolean(errors.BorrowerBankCountry)}
            helperText={errors.BorrowerBankCountry}
          />
        ) : (
          <A_Select
            id="BorrowerBankCountry"
            label="Creditor Bank Country"
            fullWidth
            variant="standard"
            required={true}
            isCascading={true}
            defaultValue={formData.BorrowerBankCountry || ""}
            options={getEnumOptions("Country")}
            onChange={(selectedOption) =>
              handleSelectChange(selectedOption, "BorrowerBankCountry")
            }
            disabled={isSortCodeLookupLoading || isBankDetailsReadOnly}
            error={Boolean(errors.BorrowerBankCountry)}
            helperText={errors.BorrowerBankCountry}
          />
        )}
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerBankAddress"
          name="BorrowerBankAddress"
          label="Creditor Bank Address"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerBankAddress}
          inputType="value"
          onChange={(e) => handleTextChange(e, "BorrowerBankAddress")}
          required={true}
          error={Boolean(errors.BorrowerBankAddress)}
          helperText={errors.BorrowerBankAddress}
          // disabled={isPrefilled}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerAddress"
          name="BorrowerAddress"
          label="Creditor Address"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerAddress}
          inputType="value"
          onChange={(e) => handleTextChange(e, "BorrowerAddress")}
          required={true}
          error={Boolean(errors.BorrowerAddress)}
          helperText={errors.BorrowerAddress}
          // disabled={isPrefilled}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerTown"
          name="BorrowerTown"
          label="Creditor Town"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerTown}
          inputType="value"
          onChange={(e) => handleTextChange(e, "BorrowerTown")}
          required={true}
          error={Boolean(errors.BorrowerTown)}
          helperText={errors.BorrowerTown}
          // disabled={isPrefilled}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerPostcode"
          name="BorrowerPostcode"
          label="Creditor Postcode"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerPostcode}
          inputType="value"
          onChange={(e) => handleTextChange(e, "BorrowerPostcode")}
          required={true}
          error={Boolean(errors.BorrowerPostcode)}
          helperText={errors.BorrowerPostcode}
          // disabled={isPrefilled}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_Select
          id="BorrowerPaymentCountry"
          label="Creditor Country"
          fullWidth
          variant="standard"
          required={true}
          defaultValue={formData.BorrowerPaymentCountry || ""}
          options={getEnumOptions("Country")}
          onChange={(selectedOption) =>
            handleSelectChange(selectedOption, "BorrowerPaymentCountry")
          }
          error={Boolean(errors.BorrowerPaymentCountry)}
          helperText={errors.BorrowerPaymentCountry}
          // disabled={isPrefilled}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_Select
          id="BorrowerBankVerificationStatus"
          label="Bank Verification Status"
          fullWidth
          variant="standard"
          defaultValue={formData.BorrowerBankVerificationStatus || ""}
          options={getEnumOptions("BankVerificationStatus")}
          onChange={(selectedOption) =>
            handleSelectChange(selectedOption, "BorrowerBankVerificationStatus")
          }
          error={Boolean(errors.BorrowerBankVerificationStatus)}
          helperText={errors.BorrowerBankVerificationStatus}
          disabled={!isEditMode}
        />
      </Grid>
    </Grid>
  );

  const renderThirdPartyFields = () => {
    const thirdPartyCount = parseInt(formData.ThirdPartyCount) || 0;
    const thirdPartyFields = [];

    for (let i = 0; i < thirdPartyCount; i++) {
      const key = `ThirdParty_${i}`;
      thirdPartyFields.push(
        <Grid
          container
          spacing={2}
          key={key}
          className={classes.thirdPartyCard}
        >
          <Grid item xs={12}>
            <Typography
              variant="subtitle2"
              className={classes.thirdPartyHeader}
            >
              Third Party {i + 1}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} className={classes.fieldGrid}>
            <A_Select
              id={`ThirdPartyType_${i}`}
              key={`ThirdPartyType_${i}_${
                getEnumOptions("ThirdPartyBeneficiaryType").length
              }`}
              label="Third Party Type"
              isCascading={true}
              onChange={(selectedOption) =>
                handleThirdPartyTypeChange(selectedOption, i)
              }
              required
              error={Boolean(errors[`ThirdPartyType_${i}`])}
              helperText={errors[`ThirdPartyType_${i}`]}
              variant="standard"
              defaultValue={
                formData[`ThirdPartyType_${i}`]?.value ||
                formData[`ThirdPartyType_${i}`] ||
                ""
              }
              options={getEnumOptions("ThirdPartyBeneficiaryType")}
            ></A_Select>
          </Grid>
          <Grid item xs={12} sm={6} className={classes.fieldGrid}>
            <A_Select
              id={`ThirdPartyName_${i}`}
              key={`ThirdPartyName_${i}_${getThirdPartyNameOptions(i).length}`}
              label="Third Party Name"
              isCascading={true}
              defaultValue={
                formData[`ThirdPartyName_${i}`]?.value ||
                formData[`ThirdPartyName_${i}`] ||
                ""
              }
              onChange={(selectedOption) =>
                handleInputChange(`ThirdPartyName_${i}`, selectedOption || "")
              }
              disabled={
                !formData[`ThirdPartyType_${i}`] || thirdPartyLoading[i]
              }
              required
              options={getThirdPartyNameOptions(i)}
              error={Boolean(errors[`ThirdPartyName_${i}`])}
              helperText={errors[`ThirdPartyName_${i}`]}
              variant="standard"
            ></A_Select>
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container spacing={1}>
        <Grid item xs={12} className={classes.checkboxWrapper}>
          <A_CheckBox
            label="Add Third Party Details"
            checked={!!formData.HasThirdParty}
            onChange={(value) => handleInputChange("HasThirdParty", value)}
            id="HasThirdParty"
            name="HasThirdParty"
          />
        </Grid>
        {formData.HasThirdParty && (
          <>
            <Grid item xs={12} className={classes.countFieldWrapper}>
              <A_TextField
                required
                id={"ThirdPartyCount"}
                fullWidth
                label="Third Party Count (Max 5)"
                defaultValue={formData.ThirdPartyCount || ""}
                onChange={(e) => handleThirdPartyCountChange(e.target.value)}
                type="number"
                inputProps={{ min: 1, max: 5 }}
                variant="standard"
                error={Boolean(errors.ThirdPartyCount)}
                helperText={errors.ThirdPartyCount}
              />
            </Grid>
            {thirdPartyFields}
          </>
        )}
      </Grid>
    );
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderBorrowerDetailsFields();
      case 1:
        return renderThirdPartyFields();
      default:
        return null;
    }
  };

  return (
    <div className={classes.root}>
      <Paper elevation={0} className={classes.formCard}>
        <div className={classes.stepperWrap}>
          {apiError && (
            <Box
              mb={2}
              style={{
                backgroundColor: "#fff3cd",
                padding: "10px",
                borderRadius: "calc(4px * var(--app-radius-scale, 1))",
              }}
            >
              <Typography color="error" variant="body2">
                {apiError}
              </Typography>
            </Box>
          )}
          <Stepper activeStep={activeStep}>
            {PAYMENT_DETAILS_STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </div>

        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress />
          </Box>
        ) : (
          <div className={classes.fieldsContainer}>{renderStepContent()}</div>
        )}

        <div className={classes.actions}>
          <div className={classes.actionGroup}>
            {activeStep !== 0 && (
              <A_Button
                label="Back"
                onClick={handleBack}
                premiumVariant="secondary"
                className={classes.actionButton}
              />
            )}
          </div>
          <A_Button
            label={
              activeStep === PAYMENT_DETAILS_STEPS.length - 1
                ? "Finish"
                : "Next"
            }
            onClick={handleNext}
            premiumVariant="primary"
            disabled={isSubmitting}
            className={classes.actionButton}
          />
        </div>
      </Paper>
    </div>
  );
});

AddPaymentDetails.displayName = "AddPaymentDetails";

AddPaymentDetails.propTypes = {
  onClose: PropTypes.func,
  afterClose: PropTypes.any,
  setToast: PropTypes.func,
  isEditMode: PropTypes.bool,
  recordId: PropTypes.string,
};

const mapStateToProps = (state) => ({
  setToast: state.toast.setToast,
});

const mapDispatchToProps = (dispatch) => ({
  setToast: bindActionCreators(toastActions.setToast, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddPaymentDetails);
